import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment-timezone';
import { store } from '../../../redux_setup/store';
import {
  setSelectedSavedWorkout,
  setActiveModal,
  setIsDragging,
  setDragSource,
  setDraggedItem,
  setOnBeforeCapture,
  setIsCloneDragging,
  setCalendarItems,
  setNonDateBasedProgramItems,
  setCombineWithInfo,
  updateCalendarItemsOrder,
  updateWorkoutItemsOrder,
  addPreSavedItemToCalendarItemsArray,
  addPreSavedItemToProgramItemsArray,
  setPlaceholderInfo,
  setDragIsInProgress,
  setDroppableContainerCurrentlyHoveredByDraggedItem,
  moveExerciseBetweenDatesMultiEnabled,
  moveNonDateBasedExerciseBetweenDays,
  removeExpandedItem,
} from '../ducks/calendarActions';
import generateUniqueId from '../../../shared/utils/generateUniqueId';
import { SUPERSET_DEFAULTS } from '../../../shared/constants';
import { caches } from '../utils/virtualizationCache';

const useDragManager = () => {
  const dispatch = useDispatch();

  // Get calendar items without subscribing
  const getCalendarItems = useCallback((date, programDayNumber) => {
    const state = store.getState();
    const { programType } = state.calendar.data;
    const dateFormatted = moment(date).format('YYYY-MM-DD');
    if (programType === 'free-form-program') {
      return state.calendar.data.nonDateBasedProgramItems[
        programDayNumber
      ]?.exercises || [];
    }
    return state.calendar.data.calendarItems[dateFormatted]?.exercises || [];
  }, []);

  const getAllExerciseCalendarItemsForWeekOrDay = useCallback((itemsDictionary) => {
    const calendarItemsArray = Object.values(itemsDictionary);

    // Use flatMap to extract and flatten all exercises arrays into one array
    const allExercises = calendarItemsArray.flatMap((itm) => itm.exercises || []);

    return allExercises;
  }, []);

  // Get expanded items without subscribing
  const getExpandedItems = useCallback(() => {
    const state = store.getState();
    return state.calendar.data.expandedItems;
  }, []);

  // Get selected items without subscribing
  const getSelectedItems = useCallback(() => {
    const state = store.getState();
    return state.calendar.data.selectedItems;
  }, []);

  // Get other data without subscribing
  const getDragData = useCallback(() => {
    const state = store.getState();
    return {
      currentUser: state.auth.data.currentUser,
      selectedCalendar: state.calendar.data.selectedReduxCalendar,
      selectedAthlete: state.calendar.data.selectedReduxAthlete,
      savedWorkouts: state.calendar.data.savedWorkouts,
      calendarItemContainers: state.calendar.data.calendarItemContainers,
      programType: state.calendar.data.programType,
    };
  }, []);

  // Helper function to get calendar items for week-day containers
  const getCalendarItemsForDroppable = useCallback((
    droppableId,
    selectedDate,
    selectedProgramDayNumber,
  ) => {
    if (droppableId === 'programmedCalendarItems') {
      return getCalendarItems(selectedDate, selectedProgramDayNumber);
    }
    // Handle week-day containers
    if (droppableId.startsWith('week-day-')) {
      const targetDate = droppableId.replace('week-day-', '');
      const targetDayNumber = droppableId.replace('week-day-', '');
      return getCalendarItems(targetDate, targetDayNumber);
    }

    // Handle sidebar containers
    const dragData = getDragData();
    return dragData.calendarItemContainers[droppableId] || [];
  }, [getCalendarItems, getDragData]);

  // Helper function to get the correct date for a droppable container
  const getDateForDroppable = useCallback((droppableId, selectedDate) => {
    if (droppableId === 'programmedCalendarItems') {
      return selectedDate;
    }

    // Handle week-day containers
    if (droppableId.startsWith('week-day-')) {
      const dateForDroppable = droppableId.replace('week-day-', '');
      return dateForDroppable;
    }

    // Default to selected date for other containers
    return selectedDate;
  }, []);

  // Get next available grouping letter
  const getNextAvailableGrouping = useCallback((calendarItems) => {
    const alphabet = Object.keys(SUPERSET_DEFAULTS);

    // return grouping letter and filter them -  checks if the current letter is the first time
    // that letter appears in the array. If it is, the condition will be true, and that letter
    // will be included in the filtered result.
    const usedLetters = calendarItems
      .map((item) => item.groupingLetter && item.groupingLetter.toUpperCase())
      .filter((val, index, self) => val && self.indexOf(val) === index);

    const nextLetter = alphabet.find((letter) => usedLetters.indexOf(letter) === -1) || 'A';

    return {
      groupingLetter: nextLetter,
      groupingColorCode: SUPERSET_DEFAULTS[nextLetter],
    };
  }, []);

  const handleCombineLogic = useCallback((
    result,
    sourceItems,
    targetItems,
    dragData,
    selectedDate,
    selectedProgramDayNumber,
    viewType,
    programType,
  ) => {
    const { source, combine, draggableId } = result;
    const { accountCode } = dragData.currentUser;
    const sourceDate = getDateForDroppable(source.droppableId, selectedDate);
    const targetDate = getDateForDroppable(combine.droppableId, selectedDate);

    const sourceDayNumber = getDateForDroppable(source.droppableId, selectedProgramDayNumber);
    const targetDayNumber = getDateForDroppable(combine.droppableId, selectedProgramDayNumber);

    // Track original items
    const originalSourceItems = sourceItems.map((item) => ({ ...item }));
    const originalTargetItems = targetItems.map((item) => ({ ...item }));

    // Create new array to manipulate items and set with grouping letters and colors
    const newSourceItems = [...sourceItems];
    const newTargetItems = [...targetItems];
    let sourceItem = {};
    const draggableIdString = combine.draggableId;
    const cleanDraggableAssignedId = draggableIdString.replace(/-programmedCalendarItems$/, '');

    // Check if source is from sidebar
    const sidebarSources = [
      'liftCalendarItems', 'warmUpCalendarItems', 'SAQCCalendarItems',
      'circuitCalendarItems', 'noteCalendarItems', 'questionnaireCalendarItems',
      'coolDownCalendarItems', 'healthCalendarItems', 'progressionCalendarItems',
      'tagCalendarItems', 'accessoryCalendarItems',
    ];

    if (sidebarSources.includes(source.droppableId)) {
      const sideBarItemsWhereSourceComingFrom = dragData.calendarItemContainers[source.droppableId];
      const removedDraggableIdMinusSideBarFlag = draggableId.match(/^(.*)-sideBar$/i)
        ? draggableId.match(/^(.*)-sideBar$/i)[1]
        : null;
      const removed = sideBarItemsWhereSourceComingFrom.find(
        (item) => item.id === removedDraggableIdMinusSideBarFlag,
      );

      sourceItem = {
        ...removed,
        title: removed.name,
        exerciseId: parseInt(removed.id, 10),
        id: `${removed.id}-${generateUniqueId()}`,
        assignedId: `${removed.id}-${generateUniqueId()}`,
        preSave: true,
        source: 'sidebar',
      };
    } else {
      /** Source item is in programmed calendar items */
      sourceItem = newSourceItems[source.index];
    }

    const targetItemIndex = targetItems.findIndex(
      (item) => item.assignedId.toString() === cleanDraggableAssignedId.toString(),
    );
    const targetItem = targetItems[targetItemIndex];
    const originalSource = originalSourceItems[source.index];
    const originalTargetIndex = originalTargetItems.findIndex(
      (item) => item.assignedId.toString() === cleanDraggableAssignedId.toString(),
    );
    const originalTarget = originalTargetItems[originalTargetIndex];

    /** can not combine with session break items */
    if (targetItem?.type === 'session_break' || sourceItem?.type === 'session_break') {
      return;
    }
    if (sourceItem?.type === 'session_break' && sourceItem.source === 'sidebar') {
      return;
    }
    // Handle grouping logic
    if (!targetItem.groupingLetter && !sourceItem.groupingLetter && targetItem.type !== 'session_break' && sourceItem.type !== 'session_break' && targetItem.type !== 'questionnaire' && sourceItem.type !== 'questionnaire') {
      const { groupingLetter, groupingColorCode } = getNextAvailableGrouping(newTargetItems);
      targetItem.groupingLetter = groupingLetter;
      targetItem.groupingColorCode = groupingColorCode;
      targetItem.shouldUpdateGrouping = groupingLetter;
      sourceItem.groupingLetter = groupingLetter;
      sourceItem.groupingColorCode = groupingColorCode;
      sourceItem.shouldUpdateGrouping = groupingLetter;
    }

    if (targetItem.groupingLetter && !sourceItem.groupingLetter && targetItem.type !== 'session_break' && sourceItem.type !== 'session_break' && targetItem.type !== 'questionnaire' && sourceItem.type !== 'questionnaire') {
      sourceItem.groupingLetter = targetItem.groupingLetter;
      sourceItem.groupingColorCode = targetItem.groupingColorCode;
      sourceItem.shouldUpdateGrouping = targetItem.groupingLetter;
    }

    if (sourceItem.groupingLetter && !targetItem.groupingLetter && targetItem.type !== 'session_break' && sourceItem.type !== 'session_break' && targetItem.type !== 'questionnaire' && sourceItem.type !== 'questionnaire') {
      const { groupingLetter, groupingColorCode } = getNextAvailableGrouping(newTargetItems);
      targetItem.groupingLetter = groupingLetter;
      targetItem.groupingColorCode = groupingColorCode;
      targetItem.shouldUpdateGrouping = groupingLetter;
      sourceItem.groupingLetter = groupingLetter;
      sourceItem.groupingColorCode = groupingColorCode;
      sourceItem.shouldUpdateGrouping = groupingLetter;
    }

    if (sourceItem.groupingLetter && targetItem.groupingLetter && targetItem.type !== 'session_break' && sourceItem.type !== 'session_break' && targetItem.type !== 'questionnaire' && sourceItem.type !== 'questionnaire') {
      sourceItem.groupingLetter = targetItem.groupingLetter;
      sourceItem.groupingColorCode = targetItem.groupingColorCode;
      sourceItem.shouldUpdateGrouping = targetItem.groupingLetter;
    }

    if (source.droppableId === combine.droppableId) {
      /** Move within the same array, adjusting for index shifts */
      const [movedItem] = newSourceItems.splice(source.index, 1);
      const adjustedTargetIndex = source.index < targetItemIndex
        ? targetItemIndex - 1
        : targetItemIndex;

      newSourceItems.splice(adjustedTargetIndex + 1, 0, movedItem);
    } else {
      /** moving between droppable containers */
      if (sourceItem.source === 'sidebar') {
        /** Coming from sidebar — don't remove, just insert into target */
        newTargetItems.splice(targetItemIndex + 1, 0, sourceItem);

        if (programType === 'free-form-program') {
          dispatch(setNonDateBasedProgramItems(newTargetItems, Number(targetDayNumber)));
        } else {
          dispatch(setCalendarItems(newTargetItems, moment(targetDate).format('YYYY-MM-DD')));
        }

        /** this will trigger an update call in the
         * appropriate tables with new grouping letter and color */
        dispatch(setCombineWithInfo({
          /** if either the target item of the sourceItem grouping letter is the same */
          /** as the original then we don't need to set something to trigger an update */
          target: originalTarget?.groupingLetter === targetItem?.groupingLetter
            ? null : targetItem,
          source: originalSource?.groupingLetter === sourceItem?.groupingLetter
            ? null : [sourceItem],
        }));
      }
      if (sourceItem.source !== 'sidebar') {
        /** Move from source list to target list */
        const [movedItem] = newSourceItems.splice(source.index, 1);
        newTargetItems.splice(targetItemIndex + 1, 0, movedItem);
      }
    }

    /** Update Redux and setCombineWithInfo and hit moveExerciseBetween days if necessary
     * reorder endpoint will get hit on update if the order has changed
     * this will set the new values on the items that got updated and it will
     * set the new order so that the source item gets removed and replaced
     * right underneath the target item. */
    if (source.droppableId === combine.droppableId && sourceItem.source !== 'sidebar') {
      if (programType === 'free-form-program') {
        dispatch(setNonDateBasedProgramItems(newSourceItems, Number(sourceDayNumber)));
      } else {
        dispatch(setCalendarItems(newSourceItems, moment(sourceDate).format('YYYY-MM-DD')));
      }

      /** this will trigger an update call in the appropriate
       * tables with new grouping letter and color */
      dispatch(setCombineWithInfo({
        /** if either the target item of the sourceItem grouping letter is the same */
        /** as the original then we don't need to set something to trigger an update */
        target: originalTarget?.groupingLetter === targetItem?.groupingLetter ? null : targetItem,
        source: originalSource?.groupingLetter === sourceItem?.groupingLetter ? null : [sourceItem],
      }));
    }
    /** combined with an item not in the same day */
    if (source.droppableId !== combine.droppableId && sourceItem.source !== 'sidebar') {
      const assignmentId = sourceItem?.assignedId;
      const newEndItems = newTargetItems;
      if (programType === 'free-form-program') {
        dispatch(setNonDateBasedProgramItems(newSourceItems, Number(sourceDayNumber)));
        dispatch(setNonDateBasedProgramItems(newTargetItems, Number(targetDayNumber)));
      } else {
        dispatch(setCalendarItems(newSourceItems, moment(sourceDate).format('YYYY-MM-DD')));
        dispatch(setCalendarItems(newTargetItems, moment(targetDate).format('YYYY-MM-DD')));
      }
      if (!sourceItem.preSave) {
        if (programType === 'free-form-program') {
          const assignedIds = [assignmentId];
          /** if the view type is free form program we need to hit the program library endpoint */
          dispatch(moveNonDateBasedExerciseBetweenDays(
            accountCode,
            assignedIds,
            targetDayNumber,
            sourceDayNumber,
            targetDayNumber,
            newEndItems,
            {
              onSuccess: () => {
              /** setting items here again helps make sure we don't get a temporary flicker back to
               * the presaved grouping items and colors
               */
                dispatch(setNonDateBasedProgramItems(newSourceItems, Number(sourceDayNumber)));
                dispatch(setNonDateBasedProgramItems(newTargetItems, Number(targetDayNumber)));

                /** this will trigger an update call in the
                 * appropriate tables with new grouping letter and color */
                dispatch(setCombineWithInfo({
                /** if either the target item of the sourceItem grouping letter is the same */
                /** as the original then we don't need to set something to trigger an update */
                  target: originalTarget?.groupingLetter === targetItem?.groupingLetter
                    ? null : targetItem,
                  source: originalSource?.groupingLetter === sourceItem?.groupingLetter
                    ? null : [sourceItem],
                }));
              },
              onError: () => {},
            },
          )).catch(() => {
            dispatch(setNonDateBasedProgramItems(originalSourceItems, Number(sourceDayNumber)));
            dispatch(setNonDateBasedProgramItems(originalTargetItems, Number(targetDayNumber)));
          });
        } else {
          /** if the viewType is calendar we need to hit hte calendar move endpoint */
          const assignedIds = [assignmentId];
          dispatch(moveExerciseBetweenDatesMultiEnabled(
            accountCode,
            assignedIds,
            targetDate,
            sourceDate,
            targetDate,
            newEndItems,
            {
              onSuccess: () => {
              /** setting items here again helps make sure we don't get a temporary flicker back to
               * the presaved grouping items and colors
               */
                dispatch(setCalendarItems(newSourceItems, moment(sourceDate).format('YYYY-MM-DD')));
                dispatch(setCalendarItems(newTargetItems, moment(targetDate).format('YYYY-MM-DD')));

                /** this will trigger an update call in the
                 * appropriate tables with new grouping letter and color */
                dispatch(setCombineWithInfo({
                /** if either the target item of the sourceItem grouping letter is the same */
                /** as the original then we don't need to set something to trigger an update */
                  target: originalTarget?.groupingLetter === targetItem?.groupingLetter
                    ? null : targetItem,
                  source: originalSource?.groupingLetter === sourceItem?.groupingLetter
                    ? null : [sourceItem],
                }));
              },
              onError: () => {},
            },
          )).catch(() => {
            dispatch(setCalendarItems(originalSourceItems, moment(sourceDate).format('YYYY-MM-DD')));
            dispatch(setCalendarItems(originalTargetItems, moment(targetDate).format('YYYY-MM-DD')));
          });
        }
      }
    }
  }, [dispatch, getNextAvailableGrouping]);

  const handleRegularDrag = useCallback((
    result,
    currentDayItems,
    dragData,
    selectedDate,
    selectedProgramDayNumber,
  ) => {
    const { source, destination, draggableId } = result;
    const { accountCode } = dragData.currentUser;
    const expandedItems = getExpandedItems();
    const destinationDate = getDateForDroppable(destination.droppableId, selectedDate);
    const destinationDay = getDateForDroppable(destination.droppableId, selectedProgramDayNumber);
    const endpointFormattedDate = moment(destinationDate).format('YYYY-MM-DD');
    const draggableIdString = draggableId;
    const cleanDraggableAssignedId = draggableIdString.replace(/-programmedCalendarItems$/, '');

    if (!destination) return;

    /** Early returns for same droppable sources */
    const sameDroppableSources = [
      'liftCalendarItems', 'warmUpCalendarItems', 'SAQCCalendarItems',
      'circuitCalendarItems', 'noteCalendarItems', 'questionnaireCalendarItems',
      'coolDownCalendarItems', 'healthCalendarItems', 'progressionCalendarItems',
      'tagCalendarItems', 'additional-items-container',
    ];

    if (sameDroppableSources.includes(source.droppableId)
      && source.droppableId === destination.droppableId) {
      return;
    }

    if (source.droppableId === destination.droppableId && source.index === destination.index) {
      return;
    }

    const start = getCalendarItemsForDroppable(
      source.droppableId,
      selectedDate,
      selectedProgramDayNumber,
    );
    const end = getCalendarItemsForDroppable(
      destination.droppableId,
      selectedDate,
      selectedProgramDayNumber,
    );

    /** ---------------------------------------------
       * HELPERS — prevent dropping into collapsed session breaks
       * --------------------------------------------- */

    const isIndexInsideCollapsedSessionBreak = (items, index) => {
      const item = items[index];
      if (!item) return false;

      for (let i = index - 1; i >= 0; i -= 1) {
        if (items[i].type === 'session_break') {
          return !expandedItems[items[i].assignedId];
        }
      }

      return false;
    };

    const getIndexAfterSessionBreakGroup = (items, index) => {
      let sbIndex = -1;

      for (let i = index - 1; i >= 0; i -= 1) {
        if (items[i].type === 'session_break') {
          sbIndex = i;
          break;
        }
      }

      if (sbIndex === -1) return index;

      let insertIndex = sbIndex + 1;
      for (let i = sbIndex + 1; i < items.length; i += 1) {
        if (items[i].type === 'session_break') break;
        insertIndex = i + 1;
      }

      return insertIndex;
    };

    if (start === end) {
      /** REORDERING WITHIN THE SAME LIST (SAME DAY) */

      const originalItems = Array.from(start);
      const newItems = Array.from(start);

      const currentItem = originalItems[source.index];
      let finalItems;

      /** NEW LOGIG FOR ACCOMMODATING MULTI-SELECT DRAG */
      /** we only need to handle this for same day reordering because we
       * cannot support moving multiple items as a chunk on the back end from
       * day to day
       */
      const selectedItems = getSelectedItems();
      const itemIsCheckBoxSelectedItem = selectedItems[currentItem?.assignedId] || false;
      if (itemIsCheckBoxSelectedItem && Object.keys(selectedItems || {}).length > 1) {
        const selectedItemIds = Object.keys(selectedItems || {});
        /** Step 1: find the nearest visible neighbors and no op if dragged item
         * doesn't move past a visible item
         */
        const draggedIndex = source.index;
        const destinationIndex = destination.index;

        let prevVisibleIndex = null;
        for (let i = draggedIndex - 1; i >= 0; i -= 1) {
          if (!selectedItemIds.includes(String(originalItems[i].assignedId))) {
            prevVisibleIndex = i;
            break;
          }
        }

        let nextVisibleIndex = null;
        for (let i = draggedIndex + 1; i < originalItems.length; i += 1) {
          if (!selectedItemIds.includes(String(originalItems[i].assignedId))) {
            nextVisibleIndex = i;
            break;
          }
        }

        /** NO-OP GUARD */
        /**
         * If the drop happened within the invisible selected-items gap
         * (i.e., we didn’t cross a visible item),
         * do nothing. This can happen because invisible items still exist
         * in the drag context so we need to account for those indexes
         * this can happen if we have ABD selected and we grab A and just move
         * it slightly - react dnd can interpret this as moving past the invisible
         * index that B was on... to prevent these edge cases we need to figure out
         * where the surrounding visible items (Relative to the drag item) are and if the user
         * doesn't drag the item past one of the next visible items we return because the user
         * visually didn't really try to move the drag group, they tried to just let it go
         * in it's current place
         */
        const movedDownOnlyAcrossHidden = destinationIndex > draggedIndex
        && (nextVisibleIndex === null || destinationIndex < nextVisibleIndex);

        const movedUpOnlyAcrossHidden = destinationIndex < draggedIndex
        && (prevVisibleIndex === null || destinationIndex > prevVisibleIndex);

        if (movedDownOnlyAcrossHidden || movedUpOnlyAcrossHidden) {
          finalItems = originalItems;
          return;
        }
        /** step 2: get selected items chunk including dragged item
         * put them in an array in the order that they appear in the items list */

        const selectedItemsChunk = originalItems.filter((
          item,
        ) => selectedItemIds.includes(String(item.assignedId)));

        const numberOfSelectedItemsBeforeDestinationIndex = originalItems
          .slice(0, destination.index)
          .filter((item) => selectedItemIds.includes(String(item.assignedId)))
          .length;

        /** Step 3: remove selected items */
        const filteredItems = originalItems.filter(
          (item) => !selectedItemIds.includes(String(item.assignedId)),
        );

        /** Step 4: adjust destination index */
        const draggedItemIsBeforeDestination = source.index < destination.index;

        let adjustedDestinationIndex = destination.index
        - numberOfSelectedItemsBeforeDestinationIndex
        + (draggedItemIsBeforeDestination ? 1 : 0);

        /** Step 4a: handle edge case where the destination is inside an invisible-item gap */
        const destinationItem = originalItems[destination.index];
        const destinationIsSelected = destinationItem
        && selectedItemIds.includes(String(destinationItem.assignedId));

        /** only adjust if dropping **into the invisible gap** AND moving downward */
        if (destinationIsSelected && destination.index > draggedIndex
          && !isIndexInsideCollapsedSessionBreak(originalItems, destination.index)) {
          /** find the nearest visible item above the destination */
          let lastVisibleIndexBeforeDestination = -1;
          for (let i = destination.index - 1; i >= 0; i -= 1) {
            if (!selectedItemIds.includes(String(originalItems[i].assignedId))) {
              lastVisibleIndexBeforeDestination = i;
              break;
            }
          }

          /** adjust destination index to **after the last visible item above the gap */
          if (lastVisibleIndexBeforeDestination >= 0) {
            adjustedDestinationIndex = filteredItems.findIndex(
              (item) => item.assignedId
              === originalItems[lastVisibleIndexBeforeDestination].assignedId,
            ) + 1;
          } else {
            /** if no visible item above, place at start */
            adjustedDestinationIndex = 0;
          }
        }

        /** 4b: PREVENT DROPPING INTO COLLAPSED SESSION BREAKS */
        if (isIndexInsideCollapsedSessionBreak(
          filteredItems,
          adjustedDestinationIndex,
          currentItem,
        )) {
          adjustedDestinationIndex = getIndexAfterSessionBreakGroup(
            filteredItems,
            adjustedDestinationIndex,
          );
        }

        /** Clamp to valid array bounds */
        adjustedDestinationIndex = Math.max(
          0,
          Math.min(adjustedDestinationIndex, filteredItems.length),
        );

        /** Step 5: insert the chunk */
        filteredItems.splice(adjustedDestinationIndex, 0, ...selectedItemsChunk);

        /** final reordered list */
        finalItems = filteredItems;
      } else {
      /** NEW LOGIG FOR ACCOMMODATING MOVING SESSION BREAKS AS CHUNK */
      /** DEFAULTS TO JUST MOVING THE SINGLE ITEM IF NO SESSION BREAK CHUNK */

        /** logic to accommodate moving a chunk of session break items */
        const getSessionBreakChunk = (items) => {
          const indexOfDraggedItem = items.findIndex(
            (i) => i.assignedId === currentItem.assignedId,
          );
          const startItem = items[indexOfDraggedItem];
          if (!startItem || startItem.type !== 'session_break') return [];

          const resultItems = [startItem];
          // eslint-disable-next-line no-plusplus
          for (let i = indexOfDraggedItem + 1; i < items.length; i++) {
            const calItem = items[i];
            if (calItem.type === 'session_break') break;
            resultItems.push(calItem);
          }
          return resultItems;
        };

        const sessionBreakArrayToBeMoved = currentItem?.type === 'session_break' ? getSessionBreakChunk(originalItems, source.index) : [];

        /** Determine the chunk to move and if no session break
         * items to move we just move the single item */
        const chunk = sessionBreakArrayToBeMoved?.length
          ? sessionBreakArrayToBeMoved
          : [newItems[source.index]];

        if (!chunk || chunk.length === 0) return;

        /** Remove all items in the chunk from the list */
        const filteredItems = newItems.filter((item) => !chunk.includes(item));

        /** Insert the chunk at the destination index */
        const insertIndex = destination.index;
        let finalInsertIndex = insertIndex;

        if (
          sessionBreakArrayToBeMoved?.length
       && destination.index > source.index
        ) {
        /** Adjust only when moving the chunk down */
          finalInsertIndex = insertIndex - (sessionBreakArrayToBeMoved.length - 1);
        }

        let adjustedDestinationIndex = finalInsertIndex;
        /** PREVENT SINGLE ITEM DROPPING INTO COLLAPSED SESSION BREAK and landing at top of list -
         * should land at end of session break group
         */
        if (chunk?.length === 1 && isIndexInsideCollapsedSessionBreak(
          filteredItems,
          adjustedDestinationIndex,
          currentItem,
        )) {
          adjustedDestinationIndex = getIndexAfterSessionBreakGroup(
            filteredItems,
            adjustedDestinationIndex,
          );
        }

        filteredItems.splice(adjustedDestinationIndex, 0, ...chunk);

        /** some times we can scoop up hidden session break items unintentionally
       * we need to account for that in the logic below and make sure they are handled
       * properly. The logic below takes care of this scenario...
       */
        /** STEP 1 — Get original and new chunks */
        const originalChunk = getSessionBreakChunk(originalItems, source.index);
        const newChunkAfterInsert = getSessionBreakChunk(filteredItems, destination.index);

        /** STEP 2 — Find items that were unintentionally added to the chunk */
        const unintentionallyMovedItems = [];

        const wasUnderCollapsedSessionBreak = (item, expandedItemsMap) => {
        /** Find the index of the current item in the original list */
          const itemIndex = originalItems.findIndex((i) => i.assignedId === item.assignedId);
          if (itemIndex === -1) return false;

          /** Get all items before this item */
          const previousItems = originalItems.slice(0, itemIndex);

          /** Find the last session break before this item */
          const lastSessionBreak = [...previousItems].reverse().find((i) => i.type === 'session_break');

          /** If a session break exists and is not expanded,
           * the item was under a collapsed session break */
          return lastSessionBreak ? !expandedItemsMap[lastSessionBreak.assignedId] : false;
        };

        newChunkAfterInsert.forEach((newItem) => {
          const wasInOriginal = originalChunk.some(
            (origItem) => origItem.assignedId === newItem.assignedId,
          );

          if (
            !wasInOriginal
          && wasUnderCollapsedSessionBreak(newItem, expandedItems)
          ) {
            unintentionallyMovedItems.push(newItem);
          }
        });
        /** Here we need to create a reordered array incase there are
         * unintentionally scooped up hidden items in the session break chunk
         * if it is the case then we will move the unintentionally moved items
         * as a unit above the session break they are currently under in the filtered items
         * then we can make a finalItems array to send to the endpoint */

        finalItems = filteredItems;

        if (unintentionallyMovedItems.length > 0) {
        /** Extract moved IDs as strings to avoid type mismatches */
          const movedIds = unintentionallyMovedItems.map((item) => String(item.assignedId));
          /** Remove unintentionally moved items from filteredItems */
          const newFilteredItems = filteredItems.filter(
            (item) => !movedIds.includes(String(item.assignedId)),
          );
          /** Find the index of the dragged session break in the new array */
          const draggedIndex = newFilteredItems.findIndex(
            (item) => String(item.assignedId) === String(cleanDraggableAssignedId),
          );

          if (draggedIndex === -1) {
            console.warn('Dragged session break not found in filteredItems.');
          } else {
          /** Insert the unintentionally moved items above the dragged session break */
            newFilteredItems.splice(draggedIndex, 0, ...unintentionallyMovedItems);
          }

          /** Replace filteredItems with the new ordered array */
          finalItems = newFilteredItems;
        }
      }

      /** Build the reordered list of assigned IDs */
      const newOrderedAssignmentIds = [];
      finalItems.forEach((calendarItem) => {
        if (typeof calendarItem.assignedId !== 'string') {
          newOrderedAssignmentIds.push(calendarItem.assignedId);
        }
      });

      if (newOrderedAssignmentIds.length) {
        if (dragData.programType === 'free-form-program') {
          const submissionObject = {
            dayNumber: destinationDay,
            order: newOrderedAssignmentIds,
          };
          dispatch(updateWorkoutItemsOrder(accountCode, submissionObject, originalItems));
          dispatch(setNonDateBasedProgramItems(finalItems, Number(destinationDay)));
        } else {
          const submissionObject = {
            date: endpointFormattedDate,
            order: newOrderedAssignmentIds,
          };
          dispatch(updateCalendarItemsOrder(accountCode, submissionObject, originalItems));
          dispatch(setCalendarItems(finalItems, endpointFormattedDate));
        }
      }
    } else {
      /** Moving between lists */
      const startItems = Array.from(start);
      const endItems = Array.from(end);

      const sidebarSources = [
        'liftCalendarItems', 'warmUpCalendarItems', 'SAQCCalendarItems',
        'circuitCalendarItems', 'noteCalendarItems', 'questionnaireCalendarItems',
        'coolDownCalendarItems', 'healthCalendarItems', 'progressionCalendarItems',
        'tagCalendarItems', 'accessoryCalendarItems',
      ];

      if (sidebarSources.includes(source.droppableId)) {
        // Handle drag from sidebar to calendar
        const removedDraggableIdMinusSideBarFlag = draggableId.match(/^(.*)-sideBar$/i)
          ? draggableId.match(/^(.*)-sideBar$/i)[1]
          : null;
        const removed = startItems.find((item) => item.id === removedDraggableIdMinusSideBarFlag);

        const copiedItem = {
          ...removed,
          title: removed.name,
          exerciseId: parseInt(removed.id, 10),
          id: `${removed.id}-${generateUniqueId()}`,
          assignedId: `${removed.id}-${generateUniqueId()}`,
          preSave: true,
        };

        if (dragData.programType === 'free-form-program') {
          dispatch(
            addPreSavedItemToProgramItemsArray(
              copiedItem,
              destinationDay || selectedProgramDayNumber,
              destination.index,
              destination.droppableId,
            ),
          );
        } else {
          dispatch(
            addPreSavedItemToCalendarItemsArray(
              copiedItem,
              endpointFormattedDate,
              destination.index,
              destination.droppableId,
            ),
          );
        }
      } else {
        // Handle drag between calendar containers (including week-day to week-day)
        const removed = startItems[source.index];
        const destinationDayNumber = getDateForDroppable(
          destination.droppableId,
          selectedProgramDayNumber,
        );
        const sourceDayNumber = getDateForDroppable(source.droppableId, selectedProgramDayNumber);
        // Update both source and destination
        const sourceDate = getDateForDroppable(source.droppableId, selectedDate);
        // eslint-disable-next-line no-shadow
        const destinationDate = getDateForDroppable(destination.droppableId, selectedDate);

        // Check if this is a cross-date movement in week view
        const isCrossDateMovement = sourceDate !== destinationDate
          && source.droppableId.startsWith('week-day-')
          && destination.droppableId.startsWith('week-day-')
          && dragData.programType !== 'free-form-program';

        const isCrossDayMovement = sourceDayNumber !== destinationDayNumber
          && source.droppableId.startsWith('week-day-')
          && destination.droppableId.startsWith('week-day-')
          && dragData.programType === 'free-form-program';

        if (isCrossDateMovement || isCrossDayMovement) {
          const assignmentId = removed.assignedId;
          const targetDate = moment(destinationDate).format('YYYY-MM-DD');
          const targetDayNumber = destinationDayNumber;
          const currentItem = startItems[source.index];

          const selectedItems = getSelectedItems();
          const selectedItemIds = Object.keys(selectedItems || {});
          const selectedItemsChunk = startItems.filter((
            item,
          ) => selectedItemIds.includes(String(item.assignedId)));
          const isDraggedItemInChunk = selectedItemsChunk.some(
            (i) => i.assignedId === removed.assignedId,
          );
          /** logic to accommodate moving a chunk of session break
           * items (for single day view only) */
          const getSessionBreakChunk = (items) => {
            const indexOfDraggedItem = items.findIndex(
              (i) => i.assignedId === currentItem.assignedId,
            );
            const startItem = items[indexOfDraggedItem];
            if (!startItem || startItem.type !== 'session_break') return [];

            const resultItems = [startItem];

            for (let i = indexOfDraggedItem + 1; i < items.length; i += 1) {
              const calItem = items[i];
              if (calItem.type === 'session_break') break;
              resultItems.push(calItem);
            }
            return resultItems;
          };

          const sessionBreakArrayToBeMoved = currentItem?.type === 'session_break' ? getSessionBreakChunk(startItems, source.index) : [];

          if (selectedItemsChunk.length > 1 && isDraggedItemInChunk) {
            /** multi select cross date/day move */
            const newStartItems = startItems.filter(
              (item) => !selectedItemIds.includes(String(item.assignedId)),
            );

            const newEndItems = [...endItems];

            /** PREVENT DROPPING INTO COLLAPSED SESSION BREAKS (DESTINATION DAY) */
            let adjustedDestinationIndex = destination.index;

            if (isIndexInsideCollapsedSessionBreak(endItems, destination.index, currentItem)) {
              adjustedDestinationIndex = getIndexAfterSessionBreakGroup(
                endItems,
                destination.index,
              );
            }
            newEndItems.splice(adjustedDestinationIndex, 0, ...selectedItemsChunk);

            /** optimistic update */
            if (dragData.programType === 'free-form-program') {
              dispatch(setNonDateBasedProgramItems(newStartItems, Number(sourceDayNumber)));
              dispatch(setNonDateBasedProgramItems(newEndItems, Number(destinationDayNumber)));
            } else {
              dispatch(setCalendarItems(newStartItems, moment(sourceDate).format('YYYY-MM-DD')));
              dispatch(setCalendarItems(newEndItems, moment(destinationDate).format('YYYY-MM-DD')));
            }

            /** hit MULTI endpoint */
            const assignedIds = selectedItemsChunk.map((i) => i.assignedId);
            if (dragData.programType === 'free-form-program') {
              /** make sure we filter out preSave items - which have id type of string */
              const filteredAssignedIds = assignedIds.filter((id) => typeof id !== 'string');

              if (filteredAssignedIds.length === 0) {
                return; /** nothing to move, don’t hit the endpoint */
              }

              dispatch(moveNonDateBasedExerciseBetweenDays(
                accountCode,
                filteredAssignedIds,
                targetDayNumber,
                sourceDayNumber,
                destinationDayNumber,
                newEndItems,
              )).catch(() => {
                /** Revert changes on error */
                dispatch(setNonDateBasedProgramItems(startItems, Number(sourceDayNumber)));
                dispatch(setNonDateBasedProgramItems(endItems, Number(destinationDayNumber)));
              });
            } else {
              /** Call API to persist the change */
              const filteredAssignedIds = assignedIds.filter((id) => typeof id !== 'string');

              if (filteredAssignedIds.length === 0) {
                return; /** nothing to move, don’t hit the endpoint */
              }
              dispatch(moveExerciseBetweenDatesMultiEnabled(
                accountCode,
                filteredAssignedIds,
                targetDate,
                sourceDate,
                destinationDate,
                newEndItems,
              )).catch(() => {
                /** Revert changes on error */
                dispatch(setCalendarItems(startItems, moment(sourceDate).format('YYYY-MM-DD')));
                dispatch(setCalendarItems(endItems, moment(destinationDate).format('YYYY-MM-DD')));
              });
            }
          } else if (sessionBreakArrayToBeMoved?.length > 1) {
            /** CROSS-DAY SESSION BREAK MOVE */

            /** Step 1 — Remove chunk from source day */
            const newStartItems = startItems.filter(
              (item) => !sessionBreakArrayToBeMoved.some((sb) => sb.assignedId === item.assignedId),
            );

            /** Step 2 — Insert chunk into destination day */
            const newEndItems = [...endItems];
            newEndItems.splice(destination.index, 0, ...sessionBreakArrayToBeMoved);

            /** STEP 3 — Post-insert check for scooped hidden items on destination day */

            /** Reuse your single-day getSessionBreakChunk function */
            const getSessionBreakChunkCrossDay = (items, startIndex) => {
              const startItem = items[startIndex];
              if (!startItem || startItem.type !== 'session_break') return [];

              const resultItems = [startItem];
              for (let i = startIndex + 1; i < items.length; i += 1) {
                const calItem = items[i];
                if (calItem.type === 'session_break') break;
                resultItems.push(calItem);
              }
              return resultItems;
            };

            /** Original chunk from source (already defined as sessionBreakArrayToBeMoved) */
            const originalChunk = sessionBreakArrayToBeMoved;

            /** New chunk after insert at destination index */
            const newChunkAfterInsert = getSessionBreakChunkCrossDay(
              newEndItems,
              destination.index,
            );

            const unintentionallyMovedItems = [];

            const wasUnderCollapsedSessionBreak = (item, expandedItemsMap) => {
              const itemIndex = endItems.findIndex((i) => i.assignedId === item.assignedId);
              if (itemIndex === -1) return false;

              const previousItems = endItems.slice(0, itemIndex);
              const lastSessionBreak = [...previousItems].reverse().find((i) => i.type === 'session_break');
              return lastSessionBreak ? !expandedItemsMap[lastSessionBreak.assignedId] : false;
            };

            newChunkAfterInsert.forEach((newItem) => {
              const wasInOriginal = originalChunk.some((
                orig,
              ) => orig.assignedId === newItem.assignedId);

              if (!wasInOriginal && wasUnderCollapsedSessionBreak(newItem, expandedItems)) {
                unintentionallyMovedItems.push(newItem);
              }
            });

            /** Step 4 — Reorder final array if needed */
            let finalEndItems = newEndItems;

            if (unintentionallyMovedItems.length > 0) {
              const movedIds = unintentionallyMovedItems.map((i) => String(i.assignedId));
              const filtered = newEndItems.filter((i) => !movedIds.includes(String(i.assignedId)));

              const draggedIndex = filtered.findIndex(
                (item) => String(item.assignedId) === String(cleanDraggableAssignedId),
              );

              if (draggedIndex === -1) {
                console.warn('Dragged session break not found in filteredItems.');
              } else {
                filtered.splice(draggedIndex, 0, ...unintentionallyMovedItems);
              }

              finalEndItems = filtered;
            }

            /** STEP 5 — Dispatch updates */
            if (dragData.programType === 'free-form-program') {
              dispatch(setNonDateBasedProgramItems(newStartItems, Number(sourceDayNumber)));
              dispatch(setNonDateBasedProgramItems(finalEndItems, Number(destinationDayNumber)));
            } else {
              dispatch(setCalendarItems(newStartItems, moment(sourceDate).format('YYYY-MM-DD')));
              dispatch(setCalendarItems(finalEndItems, moment(destinationDate).format('YYYY-MM-DD')));
            }

            /** STEP 6 — Hit endpoint */
            const assignedIds = sessionBreakArrayToBeMoved.map((i) => i.assignedId);
            if (dragData.programType === 'free-form-program') {
              /** make sure we filter out preSave items - which have id type of string */
              const filteredAssignedIds = assignedIds.filter((id) => typeof id !== 'string');

              if (filteredAssignedIds.length === 0) {
                return; /** nothing to move, don’t hit the endpoint */
              }

              dispatch(moveNonDateBasedExerciseBetweenDays(
                accountCode,
                filteredAssignedIds,
                targetDayNumber,
                sourceDayNumber,
                destinationDayNumber,
                newEndItems,
              )).catch(() => {
                /** Revert changes on error */
                dispatch(setNonDateBasedProgramItems(startItems, Number(sourceDayNumber)));
                dispatch(setNonDateBasedProgramItems(endItems, Number(destinationDayNumber)));
              });
            } else {
              /** Call API to persist the change */
              const filteredAssignedIds = assignedIds.filter((id) => typeof id !== 'string');

              if (filteredAssignedIds.length === 0) {
                return; /** nothing to move, don’t hit the endpoint */
              }

              dispatch(moveExerciseBetweenDatesMultiEnabled(
                accountCode,
                filteredAssignedIds,
                targetDate,
                sourceDate,
                destinationDate,
                newEndItems,
              )).catch(() => {
                /** Revert changes on error */
                dispatch(setCalendarItems(startItems, moment(sourceDate).format('YYYY-MM-DD')));
                dispatch(setCalendarItems(endItems, moment(destinationDate).format('YYYY-MM-DD')));
              });
            }
          } else {
          /** sinlge item cross date move */
          /** Optimistic update: Update local state immediately */
            const newStartItems = [...startItems];
            newStartItems.splice(source.index, 1);
            const newEndItems = [...endItems];
            newEndItems.splice(destination.index, 0, removed);

            if (dragData.programType === 'free-form-program') {
              dispatch(setNonDateBasedProgramItems(newStartItems, Number(sourceDayNumber)));
              dispatch(setNonDateBasedProgramItems(newEndItems, Number(destinationDayNumber)));
            } else {
              dispatch(setCalendarItems(newStartItems, moment(sourceDate).format('YYYY-MM-DD')));
              dispatch(setCalendarItems(newEndItems, moment(destinationDate).format('YYYY-MM-DD')));
            }

            if (!removed.preSave) {
              if (dragData.programType === 'free-form-program') {
                const assignedIds = [assignmentId];
                dispatch(moveNonDateBasedExerciseBetweenDays(
                  accountCode,
                  assignedIds,
                  targetDayNumber,
                  sourceDayNumber,
                  destinationDayNumber,
                  newEndItems,
                )).catch(() => {
                /** Revert changes on error */
                  dispatch(setNonDateBasedProgramItems(startItems, Number(sourceDayNumber)));
                  dispatch(setNonDateBasedProgramItems(endItems, Number(destinationDayNumber)));
                });
              } else {
              /** Call API to persist the change */
                const assignedIds = [assignmentId];
                dispatch(moveExerciseBetweenDatesMultiEnabled(
                  accountCode,
                  assignedIds,
                  targetDate,
                  sourceDate,
                  destinationDate,
                  newEndItems,
                )).catch(() => {
                /** Revert changes on error */
                  dispatch(setCalendarItems(startItems, moment(sourceDate).format('YYYY-MM-DD')));
                  dispatch(setCalendarItems(endItems, moment(destinationDate).format('YYYY-MM-DD')));
                });
              }
            }
          }
        } else {
          /** Original logic for same-date or non-week-view movements */
          const newStartItems = [...startItems];
          newStartItems.splice(source.index, 1);
          const newEndItems = [...endItems];
          newEndItems.splice(destination.index, 0, removed);

          if (dragData.programType === 'free-form-program') {
            // Update source container
            dispatch(setNonDateBasedProgramItems(newStartItems, Number(sourceDayNumber)));
            // Update destination container
            dispatch(setNonDateBasedProgramItems(newEndItems, Number(destinationDayNumber)));
          } else {
            // Update source container
            dispatch(setCalendarItems(newStartItems, moment(sourceDate).format('YYYY-MM-DD')));
            // Update destination container
            dispatch(setCalendarItems(newEndItems, moment(destinationDate).format('YYYY-MM-DD')));
          }
        }
      }
    }
  }, [dispatch, getDateForDroppable]);

  const handleDragEnd = useCallback((result) => {
    const { viewType } = store.getState().calendar.data;
    const { programType } = store.getState().calendar.data;
    // Clean up drag state first

    dispatch(setDragIsInProgress(false));
    dispatch(setPlaceholderInfo(null));
    dispatch(setDroppableContainerCurrentlyHoveredByDraggedItem(null));

    // onBeforeCapture event allows us to dynamically set the height
    // of the droppable container so we can dynamically show the message 'drag and drop here'
    // and allows us to dynamically add the extra space and dynamically
    // show the dropzone on first item drag

    dispatch(setOnBeforeCapture(null));

    // setIsCloneDragging(false) needs to stay at top of this function
    dispatch(setIsCloneDragging(false));
    dispatch(setIsDragging(false));
    dispatch(setDragSource(null));
    dispatch(setDraggedItem(null));

    const {
      source, destination, combine, draggableId,
    } = result;

    // Dropped outside the droppable area
    if (!destination && !combine) return;

    const dragData = getDragData();
    const { selectedDate } = store.getState().calendar.data;
    const { selectedProgramDayNumber } = store.getState().calendar.data;

    // If dragging from templates-sidebar, open the
    // load programs modal and set the selected workout
    if (source.droppableId === 'templates-sidebar') {
      const workoutId = draggableId.replace('-sideBar', '');
      const draggedWorkout = dragData.savedWorkouts.find((w) => w.id === parseInt(workoutId, 10));
      dispatch(setSelectedSavedWorkout(draggedWorkout));
      dispatch(setActiveModal('loadProgramsModal'));
      return;
    }

    // For day view, destination is always the current day
    const currentDayItems = getCalendarItems(selectedDate, selectedProgramDayNumber);

    // Handle combine (superset creation)
    if (combine) {
      const sourceItems = getCalendarItemsForDroppable(
        source.droppableId,
        selectedDate,
        selectedProgramDayNumber,
      );
      const targetItems = getCalendarItemsForDroppable(
        combine.droppableId,
        selectedDate,
        selectedProgramDayNumber,
      );

      handleCombineLogic(
        result,
        sourceItems,
        targetItems,
        dragData,
        selectedDate,
        selectedProgramDayNumber,
        viewType,
        programType,
      );
      return;
    }

    // Handle regular drag & drop
    handleRegularDrag(result, currentDayItems, dragData, selectedDate, selectedProgramDayNumber);
  }, [dispatch,
    getCalendarItems,
    getDragData,
    handleCombineLogic,
    handleRegularDrag,
    getCalendarItemsForDroppable,
    getDateForDroppable]);

  const handleDragStart = useCallback((start) => {
    const { selectedDate } = store.getState().calendar.data;
    const { viewType } = store.getState().calendar.data;
    const { programType } = store.getState().calendar.data;
    const { selectedProgramDayNumber } = store.getState().calendar.data;
    const currentDayItems = getCalendarItems(selectedDate, selectedProgramDayNumber);
    const { beforeRecurringWorkouts } = store.getState().calendar.data;
    const formatDate = (date) => `${moment(date).format('YYYY-MM-DD')}`;

    const sourceDroppableId = start.source.droppableId;
    const currentDayNumberOrDate = programType === 'free-form-program' ? selectedProgramDayNumber : formatDate(selectedDate);

    const getHasBefore = () => {
      if (viewType === 'weekView') {
        if (programType === 'free-form-program') {
          const numberString = sourceDroppableId.split('-').pop();
          const weekDayIndexNumber = parseInt(numberString, 10);
          return !!(beforeRecurringWorkouts[weekDayIndexNumber]
            && beforeRecurringWorkouts[weekDayIndexNumber].length);
        }
        if (programType === 'calendar') {
          const weekDate = sourceDroppableId.match(/\d{4}-\d{2}-\d{2}/)?.[0] || null;
          return !!(beforeRecurringWorkouts[formatDate(weekDate)]
          && beforeRecurringWorkouts[formatDate(weekDate)].length);
        }
      }
      return !!(beforeRecurringWorkouts[currentDayNumberOrDate]
        && beforeRecurringWorkouts[currentDayNumberOrDate].length);
    };

    /** using this data to set the size of the placeholder we will need
     * when dragging and dropping between containers
     */
    const hasBefore = getHasBefore();
    const { droppableId, index } = start.source;
    const height = caches[droppableId]
      ? caches[droppableId]?.getHeight(hasBefore ? index + 1 : index, 0) : 40;
    dispatch(setPlaceholderInfo({
      height,
      draggableId: start.draggableId,
      droppableId: start.source.droppableId,
    }));
    /** placeholder info logic ends */

    /** this state is only being used for the drop here section
     * we are not using the drop here section in week view so we don't need to
     * add week day logic below... this is only for day view so we can keep the
     * currentDayItems logic
     */
    if (currentDayItems.length <= 1 && viewType === 'dayView') {
      dispatch(setIsDragging(true));
      dispatch(setDragSource(start.source.droppableId));
    }
  }, [dispatch, getCalendarItems]);

  const handleOnBeforeCapture = useCallback((before) => {
    const { selectedDate } = store.getState().calendar.data;
    const calendarItemsDictionary = store.getState().calendar.data.calendarItems;
    // eslint-disable-next-line max-len
    const nonDateBasedProgramItemsDictionary = store.getState().calendar.data.nonDateBasedProgramItems;
    const { viewType } = store.getState().calendar.data;
    const { programType } = store.getState().calendar.data;
    const { selectedProgramDayNumber } = store.getState().calendar.data;
    const currentDayItems = getCalendarItems(selectedDate, selectedProgramDayNumber);

    if (viewType === 'weekView') {
      /** we ues this to detect if we should trigger
       * the onMouseEnter that allows us to scroll with a dragged
       * item in week view (over the side bar)
       * we had so use some speciall logic to trigger a drag and scroll
       * becuause react beautiful dnd does not support nexted scroll
       * and with out side bar it will not detect autoscroll on that side
       * so we have to hadd a funtion for it
       */
      dispatch(setDragIsInProgress(true));
    }

    /** This code is added so that we can track
     * what item is being dragged, we need to know if the item being dragged
     * is a session_break item and if so we are running some logic in the DraggableDivWrapper
     * to make sure that all items below a session break get expanded before the session_break
     * is dragged. When we add styling to onBeforeCapture we know is can create some performance
     * issues when there are a lot of items so this might need to be removed it
     * it's causing a big lag
     */
    const { draggableId } = before;
    const expandedItems = getExpandedItems();
    const selectedItems = getSelectedItems();

    /** Step 1: Extract the numeric ID before the dash */

    /** Step 1: Split the ID into parts */
    const idParts = draggableId.split('-');

    /** Step 2: Decide what to keep */
    let draggedAssignedId;
    if (idParts.length > 2) {
      /** More than 2 parts → keep first two parts
       * if there are more than two parts then it's a presave item
       * preSave items have two part ids
      */
      draggedAssignedId = idParts.slice(0, 2).join('-');
    } else {
      /** 1 or 2 parts → keep only the first part which is the saved item assignedId */
      /** draggedAssignedId = idParts[0]; */
      const [first] = idParts;
      draggedAssignedId = first;
    }

    const draggedItem = getAllExerciseCalendarItemsForWeekOrDay(programType === 'free-form-program' ? nonDateBasedProgramItemsDictionary : calendarItemsDictionary).find(
      (item) => item.assignedId.toString() === draggedAssignedId,
    );
    const itemIsExpanded = expandedItems[draggedItem?.assignedId] || false;
    const itemIsSelected = selectedItems[draggedItem?.assignedId] || false;

    if ((draggedItem?.type === 'session_break') || (itemIsSelected && Object.keys(selectedItems || {}).length > 1)) {
      /** collapse the session break when user starts to drag
       * this will also trigger all items underneath the session break
       * to hide/collapse under it right before drag start
       */
      if (itemIsExpanded) {
        dispatch(removeExpandedItem(draggedItem));
      }
      const sourceDroppableId = programType === 'free-form-program' ? `week-day-${draggedItem.dayNumber}` : `week-day-${draggedItem.date}`;
      dispatch(setDraggedItem({ ...draggedItem, sourceDroppableId }));
    }
    /** end of logic for tracking is dragged item is a session break */

    /** set onBeforeCapture is logic we use to display the drop here seciton in day view
     * we are only doing htis in day view so we dont' need this to fire in weekview
     */
    if (currentDayItems.length <= 1 && viewType === 'dayView') {
      dispatch(setOnBeforeCapture(before));
    }
  }, [dispatch, getCalendarItems, getAllExerciseCalendarItemsForWeekOrDay, getExpandedItems]);

  return {
    handleDragEnd,
    handleDragStart,
    handleOnBeforeCapture,
  };
};

export default useDragManager;
